import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Spin } from 'antd';
import { jwtDecode } from 'jwt-decode';
import useProtecaoDeRota from '../hooks/useProtecaoDeRota';
import useApi from '../hooks/useApi';
import PowerBIReport from '../componentes/PowerBIReport';

const LoadingSpinner = () => (
  <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }} />
);

const PaginaRelatorio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  let userId = null;
  try {
    userId = jwtDecode(token).usuario_id;
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
  }

  const { callApi } = useApi(`https://vision.alfama.com/api/view/dashboards/link/${id}/${userId}`, { 
    includeToken: true,
  });

  const verificando = useProtecaoDeRota();

  const renderReport = () => {
    if (!reportData) return null;
    return (
      <PowerBIReport 
        workspaceId={reportData.workspace_id}
        reportId={reportData.report_id}
        endpoint={`powerbi/${reportData.has_rls === 1 ? 'rls/' : ''}embed`}
        userId={userId}
      />
    );
  };

  const renderHelmet = () => {
    if (!reportData) return null;
    return (
      <Helmet>
        <title>Vision | Relatório - {reportData.nome_dashboard }</title>
        <meta name="description" content={`Visualização do relatório ${reportData.nome_dashboard || ''}`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  };

  // Função para pré-carregar scripts do PowerBI
  const preloadPowerBI = () => {
    const script = document.createElement('script');
    script.src = 'https://microsoft.github.io/PowerBI-JavaScript/demo/node_modules/powerbi-client/dist/powerbi.min.js';
    script.async = true;
    document.head.appendChild(script);

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://app.powerbi.com';
    document.head.appendChild(link);
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchReportData = async () => {
      try {
        const response = await Promise.race([
          callApi(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 10000)
          )
        ]);
        
        if (!response) throw new Error('Dados não encontrados');
        
        if (isMounted) {
          setReportData(response);
          setLoading(false);
          preloadPowerBI();
        }
      } catch (error) {
        console.error('Erro:', error);
        navigate('/erro', { 
          state: {
            mensagem: error.response?.status === 404 
              ? 'Relatório não encontrado' 
              : 'Erro ao carregar o relatório',
            codigo: error.response?.status || 500
          }
        });
      }
    };

    if (!verificando && userId) {
      fetchReportData();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [callApi, verificando, navigate, userId, id]);

  // Pré-carregamento de dados para próximos relatórios
  useEffect(() => {
    if (reportData) {
      const preloadNextReports = async () => {
        try {
          const nextId = parseInt(id) + 1;
          const prevId = parseInt(id) - 1;
          
          if (prevId > 0) {
            await callApi(`https://vision.alfama.com/api/view/dashboards/link/${prevId}/${userId}`);
          }
          
          await callApi(`https://vision.alfama.com/api/view/dashboards/link/${nextId}/${userId}`);
        } catch (error) {
          console.error('Erro ao pré-carregar relatórios:', error);
        }
      };

      preloadNextReports();
    }
  }, [reportData, id, userId, callApi]);

  if (verificando || loading) return <LoadingSpinner />;
  if (!reportData) return null;

  return (
    <>
      {renderHelmet()}
      {renderReport()}
    </>
  );
};

export default PaginaRelatorio;